import { Dialog } from '@headlessui/react';
import { X, AlertCircle, CheckCircle, Clock, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface StripeVerificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onContinueSetup: () => void;
    requirements: {
        currently_due: string[];
        eventually_due: string[];
        pending_verification: string[];
    };
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    disabledReason?: string | null;
}

// Human-readable messages for Stripe disabled reasons
const DISABLED_REASON_LABELS: Record<string, string> = {
    'requirements.past_due': 'Your payout setup is not finished yet. Complete the steps below and you can start receiving contributions.',
    'requirements.pending_verification': 'Your information is being verified by Stripe.',
    'rejected.fraud': 'Account verification failed due to fraud concerns. Please contact support.',
    'rejected.terms_of_service': 'Terms of service violation detected. Please contact support.',
    'rejected.listed': 'Account flagged for suspicious activity. Please contact support.',
    'rejected.other': 'Account verification was rejected. Please contact support for details.',
    'listed': 'Account is on a restricted list. Please contact support.',
    'under_review': 'Your account is under review by Stripe.',
    'other': 'There is an issue with your account. Please contact support.',
};

// TSF-233. `disabled_reason` being set does NOT mean Stripe refused the
// account: a brand-new Express account that has never been through the hosted
// form already carries 'requirements.past_due'. Painting that red told athletes
// their payout setup had failed when they had simply not finished it. Only
// these mean Stripe actually said no.
export function isRejectedDisabledReason(reason?: string | null): boolean {
    if (!reason) return false;
    return reason.startsWith('rejected.') || reason === 'listed' || reason === 'platform_paused';
}

// Human-readable labels for Stripe requirement fields
const REQUIREMENT_LABELS: Record<string, string> = {
    'individual.id_number': 'Tax ID / SSN',
    'individual.ssn_last_4': 'Last 4 digits of SSN',
    'individual.verification.document': 'ID verification document (passport, driver\'s license)',
    'individual.verification.additional_document': 'Additional verification document',
    'individual.email': 'Email address',
    'individual.phone': 'Phone number',
    'individual.dob.day': 'Date of birth (day)',
    'individual.dob.month': 'Date of birth (month)',
    'individual.dob.year': 'Date of birth (year)',
    'individual.address.line1': 'Street address',
    'individual.address.city': 'City',
    'individual.address.state': 'State / Province',
    'individual.address.postal_code': 'Postal code',
    'individual.first_name': 'First name',
    'individual.last_name': 'Last name',
    'business_profile.url': 'Business website',
    'business_profile.mcc': 'Business category',
    'business_profile.product_description': 'Business description',
    'external_account': 'Bank account information',
    'tos_acceptance.date': 'Terms of service acceptance',
    'tos_acceptance.ip': 'Terms of service acceptance',
    'company.verification.document': 'Business verification document',
    'company.tax_id': 'Business tax ID',
};

// Group requirements by category for better UX
function categorizeRequirements(requirements: string[]): Record<string, string[]> {
    const categories: Record<string, string[]> = {
        'Identity Verification': [],
        'Personal Information': [],
        'Bank Account': [],
        'Business Information': [],
        'Other': [],
    };

    requirements.forEach(req => {
        if (req.includes('verification.document') || req.includes('id_number') || req.includes('ssn')) {
            categories['Identity Verification'].push(req);
        } else if (req.includes('individual.') && !req.includes('verification')) {
            categories['Personal Information'].push(req);
        } else if (req.includes('external_account') || req.includes('bank')) {
            categories['Bank Account'].push(req);
        } else if (req.includes('business') || req.includes('company')) {
            categories['Business Information'].push(req);
        } else {
            categories['Other'].push(req);
        }
    });

    // Remove empty categories
    Object.keys(categories).forEach(key => {
        if (categories[key].length === 0) {
            delete categories[key];
        }
    });

    return categories;
}

function formatRequirement(req: string): string {
    return REQUIREMENT_LABELS[req] || req.replace(/_/g, ' ').replace(/\./g, ' › ');
}

// Collapsible category component
function CategorySection({
    category,
    items,
    colorClass
}: {
    category: string;
    items: string[];
    colorClass: string;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Categories with many detailed fields should be collapsible
    const isDetailedCategory = ['Personal Information', 'Other'].includes(category);

    if (!isDetailedCategory || items.length <= 2) {
        // Show items directly for simple categories or few items
        return (
            <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{category}</h4>
                <ul className="space-y-1.5">
                    {items.map((req) => (
                        <li key={req} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className={`${colorClass} mt-1`}>•</span>
                            <span>{formatRequirement(req)}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    // Collapsible for detailed categories
    return (
        <div className="mb-3">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                type="button"
            >
                {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                ) : (
                    <ChevronRight className="w-4 h-4" />
                )}
                <span>{category}</span>
                <span className="text-xs text-gray-500">({items.length} items)</span>
            </button>
            {isExpanded && (
                <ul className="mt-2 ml-6 space-y-1.5">
                    {items.map((req) => (
                        <li key={req} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className={`${colorClass} mt-1`}>•</span>
                            <span>{formatRequirement(req)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function StripeVerificationModal({
    isOpen,
    onClose,
    onContinueSetup,
    requirements,
    chargesEnabled,
    payoutsEnabled,
    disabledReason,
}: StripeVerificationModalProps) {
    // Deduplicate: if an item is in currently_due, don't show it in eventually_due
    // Stripe sometimes returns the same items in both arrays
    const currentlyDueSet = new Set(requirements.currently_due);
    const eventuallyDueUnique = requirements.eventually_due.filter(item => !currentlyDueSet.has(item));

    const hasCurrentlyDue = requirements.currently_due.length > 0;
    const hasPendingVerification = requirements.pending_verification.length > 0;
    const hasEventuallyDue = eventuallyDueUnique.length > 0;

    const isFullyVerified = !hasCurrentlyDue && !hasEventuallyDue && chargesEnabled && payoutsEnabled;

    // Can accept donations if charges are enabled and no currently_due items
    const canAcceptDonations = chargesEnabled && !hasCurrentlyDue;

    const categorizedCurrentlyDue = categorizeRequirements(requirements.currently_due);
    const categorizedEventuallyDue = categorizeRequirements(eventuallyDueUnique);

    console.log('[StripeVerificationModal] Requirements breakdown:', {
        currently_due_count: requirements.currently_due.length,
        eventually_due_count: requirements.eventually_due.length,
        eventually_due_unique_count: eventuallyDueUnique.length,
        currently_due: requirements.currently_due,
        eventually_due: requirements.eventually_due,
        eventually_due_unique: eventuallyDueUnique,
        chargesEnabled,
        payoutsEnabled,
        canAcceptDonations
    });

    const handleContinueSetup = () => {
        console.log('[StripeVerificationModal] Continue setup clicked');
        onClose();
        onContinueSetup();
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-[110]">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={onClose} />

            <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
                <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto pointer-events-auto">
                    <div className="p-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-start gap-3">
                                {isFullyVerified ? (
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                ) : isRejectedDisabledReason(disabledReason) ? (
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                        <AlertCircle className="w-6 h-6 text-red-600" />
                                    </div>
                                ) : (
                                    <div className="shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                        <Clock className="w-6 h-6 text-yellow-600" />
                                    </div>
                                )}
                                <div>
                                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                                        {isFullyVerified ? 'Payment Status: Verified! 🎉' : 'Payment Status'}
                                    </Dialog.Title>
                                    <p className="mt-1 text-sm text-gray-600">
                                        {isFullyVerified ? (
                                            'Your payment account is fully verified and ready to receive donations.'
                                        ) : hasCurrentlyDue && !canAcceptDonations ? (
                                            'A few more steps are needed before you can receive donations.'
                                        ) : hasCurrentlyDue && canAcceptDonations ? (
                                            '✓ You\'re accepting donations! Complete the remaining steps to avoid future service disruptions.'
                                        ) : hasEventuallyDue ? (
                                            '✓ You can already start using Devotion Fund and receive donations, but you\'ll need to complete verification soon to avoid service disruptions.'
                                        ) : (
                                            'Your verification is being reviewed by Stripe.'
                                        )}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Stripe actually refused the account -- the only red state. */}
                        {disabledReason && isRejectedDisabledReason(disabledReason) && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                                    <div>
                                        <h3 className="font-medium text-red-900">Account Issue</h3>
                                        <p className="mt-1 text-sm text-red-700">
                                            {DISABLED_REASON_LABELS[disabledReason] || disabledReason}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Still in flight: unfinished or under review. Never red. */}
                        {disabledReason && !isRejectedDisabledReason(disabledReason) && (
                            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <Clock className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                                    <div>
                                        <h3 className="font-medium text-yellow-900">Setup not finished</h3>
                                        <p className="mt-1 text-sm text-yellow-800">
                                            {DISABLED_REASON_LABELS[disabledReason] || disabledReason}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Capability Status */}
                        {!isFullyVerified && (
                            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        {chargesEnabled ? (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Clock className="w-4 h-4 text-yellow-600" />
                                        )}
                                        <span className="text-sm font-medium text-gray-900">Accept Payments</span>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-600">
                                        {chargesEnabled ? 'Enabled' : 'Pending verification'}
                                    </p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        {payoutsEnabled ? (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <Clock className="w-4 h-4 text-yellow-600" />
                                        )}
                                        <span className="text-sm font-medium text-gray-900">Receive Payouts</span>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-600">
                                        {payoutsEnabled ? 'Enabled' : 'Pending verification'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Currently Due Requirements */}
                        {hasCurrentlyDue && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertCircle className="w-5 h-5 text-red-600" />
                                    <h3 className="font-semibold text-gray-900">Action Required Now</h3>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    {canAcceptDonations
                                        ? 'Complete these items to maintain your payment capabilities:'
                                        : 'These items must be provided before you can receive donations:'}
                                </p>
                                {Object.entries(categorizedCurrentlyDue).map(([category, items]) => (
                                    <CategorySection
                                        key={category}
                                        category={category}
                                        items={items}
                                        colorClass="text-red-600"
                                    />
                                ))}
                            </div>
                        )}

                        {/* Eventually Due Requirements */}
                        {hasEventuallyDue && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <Clock className="w-5 h-5 text-yellow-600" />
                                    <h3 className="font-semibold text-gray-900">Action Required Soon</h3>
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    {canAcceptDonations
                                        ? 'You can currently accept donations, but these items will be required soon to avoid service disruptions:'
                                        : 'These items will be needed in the future:'}
                                </p>
                                {Object.entries(categorizedEventuallyDue).map(([category, items]) => (
                                    <CategorySection
                                        key={category}
                                        category={category}
                                        items={items}
                                        colorClass="text-yellow-600"
                                    />
                                ))}
                            </div>
                        )}

                        {/* Pending Verification */}
                        {hasPendingVerification && (
                            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <Clock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                                    <div>
                                        <h3 className="font-medium text-blue-900">Items Being Reviewed</h3>
                                        <p className="mt-1 text-sm text-blue-700">
                                            Some information is currently being verified by Stripe. This usually takes 1-3 business days.
                                        </p>
                                        <ul className="mt-2 space-y-1">
                                            {requirements.pending_verification.map((req) => (
                                                <li key={req} className="text-sm text-blue-700">
                                                    • {formatRequirement(req)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Info Box */}
                        {!isFullyVerified && (
                            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-900">
                                    <strong>Why is this needed?</strong> Stripe requires this information to comply with financial
                                    regulations and to protect both you and your donors. All information is securely encrypted and
                                    handled directly by Stripe.
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Close
                            </button>
                            {!isFullyVerified && (
                                <button
                                    type="button"
                                    onClick={handleContinueSetup}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                                >
                                    Continue Setup in Stripe
                                    <ExternalLink className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}

